// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import CustomSwitch from "src/components/shared/inputs/CustomSwitch/CustomSwitch";

// Enums
import { eyeTypeEnums } from "src/constants/enums";

type WhichEyeSectionType = {
  isRightFirst: boolean;
  eyeCheckboxes: { right: boolean; left: boolean };
  onChangeEye: (value: string) => void;
  onChangeSectionOrder: (isRightFirst: boolean) => void;
  isHidden?: boolean;
};

type EyeOptionType = {
  eye: string;
  isChecked: boolean;
  handleChange: () => void;
};

const WhichEyeSection = ({
  isRightFirst,
  eyeCheckboxes,
  onChangeSectionOrder,
  onChangeEye,
  isHidden = false,
}: WhichEyeSectionType) => {
  const { t } = useTranslation();
  const { LEFT, RIGHT, BOTH } = eyeTypeEnums;

  const bothEyesChecked = eyeCheckboxes.right && eyeCheckboxes.left;
  const rightEyeChecked = eyeCheckboxes.right && !eyeCheckboxes.left;
  const leftEyeChecked = !eyeCheckboxes.right && eyeCheckboxes.left;

  const showRightFirst = bothEyesChecked && !isHidden;

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  const EyeOption = ({ eye, isChecked, handleChange }: EyeOptionType) => {
    return (
      <Grid item display="flex" alignItems="center">
        <Radio
          size="small"
          checked={isChecked}
          onChange={handleChange}
          value="a"
          name="radio-buttons"
          inputProps={{ "aria-label": eye }}
          sx={{
            // reduce radio height
            padding: "0px 4px 0px 0px",
            "&.MuiButtonBase-root.MuiRadio-root": {
              color: "#E2772E",
            },
          }}
        />
        <Typography fontSize="12px" color="#FFF">
          {t(`${eye}`)}
        </Typography>
      </Grid>
    );
  };

  return (
    <Grid
      container
      item
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      visibility={!isHidden ? "visible" : "hidden"}
    >
      <TitleWithTooltip
        title={t("Which Eye?")}
        tooltip={t("Selects the eye(s) to test.")}
        titleStyle={textStyle}
        tooltipStyle={textStyle}
      />
      <Grid
        container
        item
        xs
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mb="4px"
      >
        <Grid
          item
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap="8px"
        >
          <EyeOption
            eye={BOTH}
            isChecked={bothEyesChecked}
            handleChange={() => {
              // reset isRightFirst to true when checking Both option
              onChangeEye(BOTH);
              onChangeSectionOrder(true);
            }}
          />
          <EyeOption
            eye={LEFT}
            isChecked={leftEyeChecked}
            handleChange={() => onChangeEye(LEFT)}
          />
          <EyeOption
            eye={RIGHT}
            isChecked={rightEyeChecked}
            handleChange={() => onChangeEye(RIGHT)}
          />
        </Grid>
      </Grid>
      <Grid
        item
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        width="100%"
        height="30px"
      >
        <FormControlLabel
          sx={{
            visibility: showRightFirst ? "visible" : "hidden",
            marginRight: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: "12px",
              marginLeft: "0px",
              color: "#FFF",

              "&.Mui-disabled": {
                color: "#5B6D7A",
              },
            },
          }}
          control={
            <CustomSwitch
              small
              checked={isRightFirst}
              onChange={(e) => onChangeSectionOrder(e.target.checked)}
            />
          }
          label={t("Right First")}
        />
      </Grid>
    </Grid>
  );
};

export default WhichEyeSection;
